import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Plus,
  BookOpen,
  Users,
  TrendingUp,
  BarChart3,
  Layers,
  FileText,
  Clock,
  CheckCircle2,
  Edit,
  Eye,
  Loader2,
  Target,
} from 'lucide-react';
import { useLearningPaths } from '@/api/hooks';

export const Route = createFileRoute('/author/')({
  component: AuthorDashboard,
});

function AuthorDashboard() {
  const navigate = useNavigate();
  const { data: learningPaths = [], isLoading } = useLearningPaths();

  // TODO: Filter to only show paths created by current user
  // For now, showing all paths for demonstration
  const myPaths = learningPaths;

  // Calculate stats
  const publishedPaths = myPaths.filter((p) => p.status === 'published').length;
  const draftPaths = myPaths.filter((p) => p.status === 'draft').length;
  const totalCourses = myPaths.reduce((sum, p) => sum + (p.course_count || 0), 0);

  const handleCreatePath = () => {
    // TODO: Create path editor route
    console.log('Create new path - route not yet implemented');
  };

  const handleCreateCourse = () => {
    navigate({ to: '/author/courses/$courseId/edit', params: { courseId: 'new' } });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-foreground mb-2">Author Dashboard</h1>
        <p className="text-muted-foreground">Create and manage your learning content</p>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid md:grid-cols-2 gap-4 mb-8"
      >
        <button
          onClick={handleCreatePath}
          className="flex items-center gap-4 p-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl text-white text-left hover:from-purple-700 hover:to-indigo-700 transition-all"
        >
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Create Learning Path</h3>
            <p className="text-white/80 text-sm">Build a guided learning journey</p>
          </div>
          <Plus className="w-6 h-6 ml-auto" />
        </button>

        <button
          onClick={handleCreateCourse}
          className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl text-left hover:border-input hover:bg-secondary/30 transition-all"
        >
          <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-indigo-500" />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-foreground">Create Course</h3>
            <p className="text-muted-foreground text-sm">Add a standalone course</p>
          </div>
          <Plus className="w-6 h-6 ml-auto text-muted-foreground" />
        </button>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
      >
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{publishedPaths}</p>
          <p className="text-sm text-muted-foreground">Published Paths</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{draftPaths}</p>
          <p className="text-sm text-muted-foreground">Draft Paths</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-indigo-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{totalCourses}</p>
          <p className="text-sm text-muted-foreground">Total Courses</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">--</p>
          <p className="text-sm text-muted-foreground">Enrolled Learners</p>
        </div>
      </motion.div>

      {/* My Learning Paths */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">My Learning Paths</h2>
          <button
            className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            View all
          </button>
        </div>

        {myPaths.length > 0 ? (
          <div className="space-y-3">
            {myPaths.slice(0, 5).map((path, idx) => (
              <motion.div
                key={path.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + idx * 0.05 }}
                className="bg-card rounded-xl border border-border p-4 flex items-center gap-4"
              >
                {/* Thumbnail */}
                {path.thumbnail_url ? (
                  <img
                    src={path.thumbnail_url}
                    alt={path.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-lg flex items-center justify-center"
                    style={{
                      background: path.color
                        ? `linear-gradient(135deg, ${path.color}, ${path.color}99)`
                        : 'linear-gradient(135deg, rgb(147, 51, 234), rgb(79, 70, 229))',
                    }}
                  >
                    <Target className="w-8 h-8 text-white" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">{path.name}</h3>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{path.course_count || 0} courses</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        path.status === 'published'
                          ? 'bg-green-500/20 text-green-400'
                          : path.status === 'draft'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {path.status}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Link
                    to="/learn/paths/$pathId"
                    params={{ pathId: path.id }}
                    className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </Link>
                  <button
                    className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    title="Edit"
                    onClick={() => console.log('Edit path:', path.id)}
                  >
                    <Edit className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button
                    className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    title="Analytics"
                    onClick={() => console.log('Analytics for:', path.id)}
                  >
                    <BarChart3 className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <div className="w-16 h-16 mx-auto bg-secondary rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Learning Paths Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first learning path to get started.
            </p>
            <button
              onClick={handleCreatePath}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all"
            >
              <Plus className="w-5 h-5" />
              Create Learning Path
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
